// Generic approval queue for the approval steps (Department / Director /
// Oversight / Export Control). Read-only on invoice data except the per-line-item GL
// account. Approve / Deny act per-invoice (green ✓ / red ✗ row buttons + the
// detail modal's action bar) and in bulk over checked rows ("<Approve> Selected"
// / "Deny Selected", app.md §8/§9). Only the BULK buttons get the "Are you sure?"
// warning; row/modal approve submits immediately, and row/modal deny opens just
// the required-comment dialog.
// Rows the API would refuse (canDecide, lib/identity.ts) are view-only: checkbox
// and Approve/Deny disabled with the reason in the tooltip.
// Hold (app.md §9): the row's lock button freezes the invoice in place — a hold
// row with a required comment. Held rows render dimmed, can't be selected or
// decided, and stay frozen until the lock is explicitly released (filled lock →
// release dialog). The API enforces it too: a decision on a held invoice is
// refused with `Blocked by an active hold`.
//
// PORT NOTE: a decision is synchronous now. It either applies — the response
// carries the new status — or it is refused and writes nothing, with the reason
// in the error. So the "submitting…" rows, the re-poll schedule, the give-up
// warning and the rejected-decision audit rows are all gone; after a decision the
// queue simply reloads. Bulk is one request whose per-invoice results say what
// applied and why not, which is reported alongside the client-side pre-filter.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Subtitle2,
  Body1,
  Caption1,
  Toast,
  ToastBody,
  ToastTitle,
  makeStyles,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular, LockClosedFilled, LockClosedRegular } from '@fluentui/react-icons'
import type { Hold, Invoice } from '../lib/types'
import type { DecisionOutcome as DecisionOutcomeValue, ReasonCategory } from '../lib/constants'
import { DecisionOutcome, DecisionStep, TOASTER_ID } from '../lib/constants'
import { createDecision, createDecisionBatch, createHold, loadActiveHolds, releaseHold } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, type Session } from '../lib/identity'
import { useQueue, useSelection } from '../lib/hooks'
import { resolveDecisionBatch, skippedNote } from '../lib/decisions'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { formatCurrency, formatDate } from '../lib/format'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner, WarningBanner } from '../components/States'
import { ScreenTitle } from '../components/ScreenTitle'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    minHeight: '40px',
  },
  spacer: { flex: 1 },
  count: { color: tokens.colorNeutralForeground2 },
  // Active hold: amber lock so a frozen row is unmissable even when dimmed.
  holdActive: { color: tokens.colorPaletteYellowForeground2 },
  // Per-company groups (Export Control File Generation — app.md §9 step 9).
  groups: { display: 'flex', flexDirection: 'column', gap: '28px' },
  groupHead: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  // Export Control's orange section rule, repeated between company groups
  // (user preference 2026-07-12; grouped mode is Export-Control-only today).
  groupRule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px' },
  // Overall sum across the groups; mirrors InvoiceQueue's per-group totals bar.
  grandTotal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
})

export interface ApprovalConfig {
  /** Empty string = no header — the host screen renders its own (Export Control). */
  title: string
  subtitle?: string
  queueStatus: string
  step: DecisionStep
  approveLabel: string
  approveDisabled?: boolean
  approveDisabledReason?: string
  showPdf: boolean
  /** Unlock the per-line-item GL controls in the detail modal. The three flags
      are the three separate grants in the server's authority matrix, and each
      screen passes exactly the ones its step holds — the API checks the editor's
      authority for the step regardless and writes the correction row.
      `editAccounts` is the COMPOSED picker (Export Control, Manual Entry);
      `editExpenseTypes` is "What is it?"; `editCostCenters` is "Who is it for?". */
  editAccounts?: boolean
  editExpenseTypes?: boolean
  editCostCenters?: boolean
  /** Show a grand-total footer under the queue (Export Control — app.md §9). */
  showTotals?: boolean
  /** Group the queue by company, each group with its own Select all +
      "<Approve> Selected" / "Deny Selected" — matching the one-CSV-per-company
      export grain (Export Control File Generation — app.md §9 step 9). The
      page-level bulk bar is hidden; actions live in the groups. */
  groupByCompany?: boolean
  /** Export Control: an approve here is not a decision write but one export
      request, which builds the files, stamps the batch and writes every Export
      Control approval server-side. When present it REPLACES the decision write
      on an approve, and it reports how many invoices advanced. It throws with
      every problem listed when the set isn't exportable — nothing is written in
      that case. */
  approveVia?: (invoices: Invoice[]) => Promise<{ appliedCount: number }>
}

type Pending = { kind: 'approve' | 'deny'; ids: string[]; bulk: boolean } | null
type HoldPending = { mode: 'hold'; invoice: Invoice } | { mode: 'release'; invoice: Invoice; hold: Hold } | null

function selectedPhrase(n: number): string {
  return n === 1 ? 'the selected invoice' : `the ${n} selected invoices`
}

function holdSummary(hold: Hold): string {
  const who = hold.held_by_name || hold.held_by_email || 'unknown'
  const when = formatDate(hold.held_at)
  return `On hold — ${who} on ${when}: “${hold.reason ?? ''}”`
}

interface Props {
  session: Session
  config: ApprovalConfig
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ApprovalScreen({ session, config, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([config.queueStatus], refreshNonce)
  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  // Per-company groups (groupByCompany screens), alphabetical with unknown
  // company last; each keeps the queue's oldest-first order within it.
  const companyGroups = useMemo(() => {
    if (!config.groupByCompany) return null
    const byName = new Map<string, Invoice[]>()
    for (const inv of filtered) {
      const name = inv.company_name ?? '—'
      const group = byName.get(name)
      if (group) group.push(inv)
      else byName.set(name, [inv])
    }
    return [...byName.entries()]
      .sort(([a], [b]) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)))
      .map(([name, rows]) => ({ name, invoices: rows }))
  }, [config.groupByCompany, filtered])

  // Mirror the API's authorization gate: rows it would refuse are view-only —
  // not selectable, Approve/Deny disabled.
  const decidableIds = useMemo(
    () => new Set(filtered.filter((i) => canDecide(session, config.step, i)).map((i) => i.id)),
    [filtered, session, config.step],
  )
  // All three approval tiers are company-scoped since 2026-08-10, so Oversight
  // belongs in the first branch too. Newly reachable: hold oversight for company
  // A and a director seat for company B, and B's invoices load into the Oversight
  // queue (they are in your RLS company set) while `canDecide` refuses them. The
  // generic sentence would tell that person they do not hold the role, which they
  // do — just not for that company, which is the thing they need to be told.
  const notAuthorizedReason =
    config.step === DecisionStep.Director ||
    config.step === DecisionStep.Department ||
    config.step === DecisionStep.Oversight
      ? "Only an approver appointed to this invoice's own company can decide on it at this step. You can view it, but your decision would be refused."
      : "You don't hold the role that decides at this step. You can view this invoice, but your decision would be refused."

  // Active holds for the loaded queue, keyed by id. A held invoice is frozen:
  // dimmed, unselectable, Approve/Deny disabled.
  const [holds, setHolds] = useState<Record<string, Hold>>({})
  const [holdsWarning, setHoldsWarning] = useState<string | null>(null)
  const reloadHolds = useCallback(async (rows: Invoice[]) => {
    try {
      // Each row says whether it has an active hold, so this reads only those.
      setHolds(await loadActiveHolds(rows))
      setHoldsWarning(null)
    } catch {
      // Fail safe: a transient read must NOT unfreeze deliberately held rows, so
      // keep the last known holds and warn without blocking.
      setHoldsWarning('Could not refresh holds — showing the last known state. Held invoices stay frozen; Refresh to re-check.')
    }
  }, [])
  // Key on a stable signature of the queue's rows: a reload hands us a fresh
  // array of the same rows, and keying on identity would refire the holds read.
  const holdReloadKey = invoices.map((i) => `${i.id}:${i.has_active_hold ? 1 : 0}`).join(',')
  useEffect(() => {
    reloadHolds(invoices)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on holdReloadKey (stable row signature), not the invoices array identity
  }, [holdReloadKey, reloadHolds])
  const holdFor = (inv: Invoice): Hold | undefined => holds[inv.id ?? '']
  const heldIds = useMemo(
    () => new Set(filtered.filter((i) => holds[i.id ?? '']).map((i) => i.id)),
    [filtered, holds],
  )

  const sel = useSelection(
    useMemo(() => [...decidableIds].filter((id) => !heldIds.has(id)), [decidableIds, heldIds]),
  )

  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  // Bumped when the detail modal changes a line item (the GL account picker), so
  // the expanded card behind it re-reads its lines. The queue's own reloads no
  // longer invalidate that cache (InvoiceQueue keys it on a row signature), and
  // a line item-only edit doesn't touch the invoice row.
  const [lineItemsNonce, setLineItemsNonce] = useState(0)
  const [pending, setPending] = useState<Pending>(null)
  const [holdPending, setHoldPending] = useState<HoldPending>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices])

  /** Finish a submit: drop the dialogs, reload the queue and the badges. */
  function settle() {
    setPending(null)
    setOpenInvoice(null)
    sel.clear()
    void reload()
    void reloadHolds(invoices)
    onQueuesChanged?.()
  }

  async function submit(
    requestedIds: string[],
    outcome: DecisionOutcomeValue,
    reason?: string,
    category?: string,
  ) {
    setSubmitting(true)
    setActionError(null)
    const approved = outcome === DecisionOutcome.Approved
    // Re-resolve the batch NOW, not when the dialog opened: the queue reloads
    // behind an open dialog, so a row may have left it, gone on hold, or moved
    // out of this user's authority since the selection was snapshotted. The API
    // would refuse those; filtering here keeps the confirmation honest about
    // what is actually being submitted (lib/decisions.ts).
    const batch = resolveDecisionBatch(
      requestedIds,
      (id) => byId.get(id),
      (inv) => canDecide(session, config.step, inv) && !holdFor(inv),
    )
    const acted = batch.eligible
    if (acted.length === 0) {
      setPending(null)
      setOpenInvoice(null)
      sel.clear()
      dispatchToast(
        <Toast>
          <ToastTitle>
            {batch.blocked.length === 0
              ? 'Those invoices were already processed — nothing was submitted.'
              : 'Nothing was submitted — those invoices are on hold or not yours to decide.'}
          </ToastTitle>
        </Toast>,
        { intent: 'info' },
      )
      setSubmitting(false)
      return
    }
    const skipped = skippedNote(batch)

    // Export Control's approve is its own request (see approveVia).
    if (approved && config.approveVia) {
      try {
        const { appliedCount } = await config.approveVia(acted)
        settle()
        dispatchToast(
          <Toast>
            <ToastTitle>
              {appliedCount === 1 ? 'Invoice approved and exported' : `${appliedCount} invoices approved and exported`}
            </ToastTitle>
            {skipped && <ToastBody>{skipped}</ToastBody>}
          </Toast>,
          { intent: 'success' },
        )
      } catch (e) {
        setActionError(errorMessage(e))
      } finally {
        setSubmitting(false)
      }
      return
    }

    // One invoice: the single-decision endpoint, whose refusal reason is the
    // error message (`Blocked by an active hold`, `Out of order — record status
    // is …`, `Not authorized for this step`).
    if (acted.length === 1) {
      try {
        await createDecision({
          invoice_id: acted[0].id,
          step: config.step,
          decision: outcome,
          reason: reason || undefined,
          reason_category: (category as ReasonCategory) || undefined,
          row_version: acted[0].row_version,
        })
        settle()
        dispatchToast(
          <Toast>
            <ToastTitle>Invoice {approved ? 'approved' : 'denied — routed to Denial Review'}</ToastTitle>
          </Toast>,
          { intent: 'success' },
        )
      } catch (e) {
        setActionError(errorMessage(e))
      } finally {
        setSubmitting(false)
      }
      return
    }

    // Bulk: one request, each invoice its own transaction. It always resolves,
    // so the report names exactly what the API refused and why.
    try {
      const result = await createDecisionBatch({
        step: config.step,
        decision: outcome,
        reason: reason || undefined,
        reason_category: (category as ReasonCategory) || undefined,
        invoice_ids: acted.map((i) => i.id),
      })
      settle()
      const refusals = result.results.filter((r) => !r.applied)
      if (refusals.length > 0) {
        const named = refusals
          .map((r) => {
            const inv = byId.get(r.invoice_id)
            const label = inv?.external_invoice_id || inv?.vendor_name || r.invoice_id
            return `${label}: ${r.error?.message ?? 'refused'}`
          })
          .join('; ')
        setActionError(
          `${result.applied_count} of ${result.results.length} went through. Not applied — ${named}`,
        )
      }
      dispatchToast(
        <Toast>
          <ToastTitle>
            {result.applied_count === 1 ? 'Invoice' : `${result.applied_count} invoices`}{' '}
            {approved ? 'approved' : 'denied — routed to Denial Review'}
          </ToastTitle>
          {skipped && <ToastBody>{skipped}</ToastBody>}
        </Toast>,
        { intent: result.rejected_count > 0 ? 'warning' : 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function submitHold(action: NonNullable<HoldPending>, reason?: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      if (action.mode === 'hold') {
        await createHold(action.invoice.id, reason ?? '')
      } else {
        await releaseHold(action.hold.id)
      }
      setHoldPending(null)
      setOpenInvoice(null)
      dispatchToast(
        <Toast>
          <ToastTitle>{action.mode === 'hold' ? 'Invoice placed on hold' : 'Hold released'}</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
      const rows = await reload({ silent: true })
      await reloadHolds(rows)
    } catch (e) {
      setActionError(`${action.mode === 'hold' ? 'Hold' : 'Release'} failed: ${errorMessage(e)}`)
    } finally {
      setSubmitting(false)
    }
  }

  // Row/modal approve commits without a confirmation — the warning is bulk-only.
  function approveNow(ids: string[]) {
    if (ids.length === 0 || config.approveDisabled) return
    submit(ids, DecisionOutcome.Approved)
  }
  function openBulkApprove(ids: string[]) {
    if (ids.length === 0 || config.approveDisabled) return
    setPending({ kind: 'approve', ids, bulk: true })
  }
  function openDeny(ids: string[], bulk = false) {
    if (ids.length === 0) return
    setPending({ kind: 'deny', ids, bulk })
  }

  const selectedIds = [...sel.selected]

  // Shared lock control: outline = place hold, filled amber = on hold (click to release).
  function lockButton(inv: Invoice, decidable: boolean, size?: 'small') {
    const hold = holdFor(inv)
    if (hold) {
      return (
        <Button
          size={size}
          appearance="subtle"
          icon={<LockClosedFilled className={s.holdActive} />}
          title={
            decidable
              ? `${holdSummary(hold)} — click to release.`
              : `${holdSummary(hold)} — you're not authorized to release it.`
          }
          aria-label="Release hold"
          disabled={submitting || !decidable}
          onClick={() => setHoldPending({ mode: 'release', invoice: inv, hold })}
        />
      )
    }
    return (
      <Button
        size={size}
        appearance="subtle"
        icon={<LockClosedRegular />}
        title={decidable ? 'Place hold — freeze this invoice until the hold is released' : notAuthorizedReason}
        aria-label="Place hold"
        disabled={submitting || !decidable}
        onClick={() => setHoldPending({ mode: 'hold', invoice: inv })}
      />
    )
  }

  // One queue rendering shared by the grouped (per-company) and ungrouped
  // paths — only the row set, Select all wiring, and totals label differ.
  function renderQueue(
    rows: Invoice[],
    opts: { allSelected: boolean; onToggleAll: () => void; totalsLabel?: string },
  ) {
    return (
      <InvoiceQueue
        invoices={rows}
        columns="amounts"
        selectable
        selectedIds={sel.selected}
        onToggle={sel.toggle}
        onToggleAll={opts.onToggleAll}
        allSelected={opts.allSelected}
        onOpen={setOpenInvoice}
        rowActions={(inv) => {
          const decidable = decidableIds.has(inv.id)
          const hold = holdFor(inv)
          const heldTitle = hold ? `${holdSummary(hold)} — release the hold to act.` : undefined
          return (
            <>
              {lockButton(inv, decidable, 'small')}
              <Button
                size="small"
                className={btn.approve}
                icon={<CheckmarkRegular />}
                title={
                  heldTitle ??
                  (!decidable ? notAuthorizedReason : config.approveDisabled ? config.approveDisabledReason : config.approveLabel)
                }
                aria-label={config.approveLabel}
                disabled={submitting || config.approveDisabled || !decidable || !!hold}
                onClick={() => approveNow([inv.id])}
              />
              <Button
                size="small"
                className={btn.deny}
                icon={<DismissRegular />}
                title={heldTitle ?? (decidable ? 'Deny' : notAuthorizedReason)}
                aria-label="Deny"
                disabled={submitting || !decidable || !!hold}
                onClick={() => openDeny([inv.id])}
              />
            </>
          )
        }}
        selectionDisabled={(inv) =>
          holdFor(inv)
            ? `${holdSummary(holdFor(inv)!)} — held invoices are excluded from bulk actions.`
            : decidableIds.has(inv.id)
              ? undefined
              : notAuthorizedReason
        }
        dimmedIds={heldIds}
        heldIds={heldIds}
        lineItemsNonce={lineItemsNonce}
        totalsLabel={opts.totalsLabel}
      />
    )
  }

  return (
    <div>
      {(config.title || config.subtitle) && (
        <div className={s.header}>
          {config.title && <ScreenTitle>{config.title}</ScreenTitle>}
          {config.subtitle && <Body1 className={s.sub}>{config.subtitle}</Body1>}
        </div>
      )}

      <ErrorBanner message={error || actionError} />
      <WarningBanner message={holdsWarning} onDismiss={() => setHoldsWarning(null)} />

      {!config.groupByCompany && (
        <div className={s.actionBar}>
          <span className={s.count}>{sel.count} selected</span>
          <div className={s.spacer} />
          {config.approveDisabled && config.approveDisabledReason && (
            <Caption1>{config.approveDisabledReason}</Caption1>
          )}
          <Button
            className={btn.approve}
            icon={<CheckmarkRegular />}
            disabled={sel.count === 0 || submitting || config.approveDisabled}
            title={config.approveDisabled ? config.approveDisabledReason : undefined}
            onClick={() => openBulkApprove(selectedIds)}
          >
            {config.approveLabel} Selected
          </Button>
          <Button
            className={btn.deny}
            icon={<DismissRegular />}
            disabled={sel.count === 0 || submitting}
            onClick={() => openDeny(selectedIds, true)}
          >
            Deny Selected
          </Button>
        </div>
      )}

      {loading ? (
        <Loading label="Loading invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : companyGroups ? (
        <div className={s.groups}>
          {companyGroups.map((g, idx) => {
            const groupIds = g.invoices.map((i) => i.id)
            const selectableIds = groupIds.filter((id) => decidableIds.has(id) && !heldIds.has(id))
            const groupSelected = groupIds.filter((id) => sel.selected.has(id))
            const groupAllSelected =
              selectableIds.length > 0 && selectableIds.every((id) => sel.selected.has(id))
            return (
              <Fragment key={g.name}>
                {idx > 0 && <div className={s.groupRule} aria-hidden />}
                <section>
                <div className={s.groupHead}>
                  <Subtitle2>{g.name}</Subtitle2>
                  <span className={s.count}>
                    {groupSelected.length} of {g.invoices.length} selected
                  </span>
                  <div className={s.spacer} />
                  {config.approveDisabled && config.approveDisabledReason && (
                    <Caption1>{config.approveDisabledReason}</Caption1>
                  )}
                  <Button
                    size="small"
                    className={btn.approve}
                    icon={<CheckmarkRegular />}
                    disabled={groupSelected.length === 0 || submitting || config.approveDisabled}
                    title={config.approveDisabled ? config.approveDisabledReason : undefined}
                    onClick={() => openBulkApprove(groupSelected)}
                  >
                    {config.approveLabel} Selected
                  </Button>
                  <Button
                    size="small"
                    className={btn.deny}
                    icon={<DismissRegular />}
                    disabled={groupSelected.length === 0 || submitting}
                    onClick={() => openDeny(groupSelected, true)}
                  >
                    Deny Selected
                  </Button>
                </div>
                  {renderQueue(g.invoices, {
                    allSelected: groupAllSelected,
                    onToggleAll: () =>
                      sel.setMany(groupAllSelected ? groupIds : selectableIds, !groupAllSelected),
                    totalsLabel: config.showTotals ? 'Company total' : undefined,
                  })}
                </section>
              </Fragment>
            )
          })}
          {config.showTotals && <div className={s.groupRule} aria-hidden />}
          {config.showTotals && (
            <div className={s.grandTotal}>
              <span>
                Grand total — {filtered.length} invoice{filtered.length === 1 ? '' : 's'}
              </span>
              <span>
                {formatCurrency(filtered.reduce((sum, i) => sum + (i.subtotal ?? 0), 0))} Subtotal +{' '}
                {formatCurrency(filtered.reduce((sum, i) => sum + (i.tax ?? 0), 0))} Tax ={' '}
                {formatCurrency(filtered.reduce((sum, i) => sum + (i.total ?? 0), 0))} Total
              </span>
            </div>
          )}
        </div>
      ) : (
        renderQueue(filtered, {
          allSelected: sel.allSelected,
          onToggleAll: sel.toggleAll,
          totalsLabel: config.showTotals ? 'Grand total' : undefined,
        })
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={config.showPdf}
        user={user}
        editAccounts={config.editAccounts}
        editExpenseTypes={config.editExpenseTypes}
        editCostCenters={config.editCostCenters}
        // A GL edit in the modal must reach the card behind it: refresh the row
        // and invalidate its cached line items. Without this the card kept
        // showing the pre-edit account until a full Refresh, which also
        // collapses every expanded card.
        onChanged={() => {
          setLineItemsNonce((n) => n + 1)
          void reload({ silent: true })
        }}
        onClose={() => setOpenInvoice(null)}
        actions={
          openInvoice &&
          (() => {
            const decidable = canDecide(session, config.step, openInvoice)
            const hold = holdFor(openInvoice)
            const heldTitle = hold ? `${holdSummary(hold)} — release the hold to act.` : undefined
            return (
              <>
                {lockButton(openInvoice, decidable)}
                <Button
                  className={btn.approve}
                  icon={<CheckmarkRegular />}
                  disabled={submitting || config.approveDisabled || !decidable || !!hold}
                  title={
                    heldTitle ??
                    (!decidable ? notAuthorizedReason : config.approveDisabled ? config.approveDisabledReason : undefined)
                  }
                  onClick={() => approveNow([openInvoice.id])}
                >
                  {config.approveLabel}
                </Button>
                <Button
                  className={btn.deny}
                  icon={<DismissRegular />}
                  disabled={submitting || !decidable || !!hold}
                  title={heldTitle ?? (decidable ? undefined : notAuthorizedReason)}
                  onClick={() => openDeny([openInvoice.id])}
                >
                  Deny
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!pending}
        title={pending?.bulk ? 'Are you sure?' : 'Deny this invoice?'}
        body={
          !pending
            ? undefined
            : pending.kind === 'approve'
              ? `Approving will immediately move ${selectedPhrase(pending.ids.length)} to the next step. You cannot undo this action.`
              : pending.bulk
                ? `Denying will immediately send ${selectedPhrase(pending.ids.length)} to Denial Review. You cannot undo this action. This comment is recorded on every selected invoice and is visible in the decision history.`
                : 'Denied invoices route to Denial Review. This comment is recorded on the invoice and is visible in the decision history.'
        }
        requireReason={pending?.kind === 'deny'}
        reasonLabel="Reason for denial"
        showReasonCategory={pending?.kind === 'deny'}
        confirmLabel={pending?.kind === 'approve' ? config.approveLabel : 'Deny'}
        confirmClassName={pending?.kind === 'approve' ? btn.approve : undefined}
        destructive={pending?.kind === 'deny'}
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={(reason, category) =>
          pending && submit(pending.ids, pending.kind === 'approve' ? DecisionOutcome.Approved : DecisionOutcome.Denied, reason, category)
        }
      />

      <DecisionDialog
        open={!!holdPending}
        title={holdPending?.mode === 'hold' ? 'Place this invoice on hold?' : 'Release this hold?'}
        body={
          !holdPending
            ? undefined
            : holdPending.mode === 'hold'
              ? 'The invoice stays in place and cannot be approved, denied, or bulk-selected until the hold is released. This comment is recorded on the hold.'
              : `${holdSummary(holdPending.hold)}. Releasing makes the invoice actionable again.`
        }
        requireReason={holdPending?.mode === 'hold'}
        reasonLabel="Reason for hold"
        confirmLabel={holdPending?.mode === 'hold' ? 'Hold' : 'Release'}
        submitting={submitting}
        onCancel={() => setHoldPending(null)}
        onConfirm={(reason) => holdPending && submitHold(holdPending, reason)}
      />
    </div>
  )
}
